import { useRef, useState } from "react";
import {
  MessageCircle, Send, Music, Heart, History, Clock, Radio, Tv
} from "lucide-react";

const Divider = ({ pc }) => (
  <div className="h-[2px] rounded-full" style={{ background: `linear-gradient(to right, ${pc}, ${pc}33, transparent)` }} />
);

export const HistorySection = ({ history, pc }) => (
  <section id="section-history" className="py-8" data-testid="history-section">
    <Divider pc={pc} />
    <div className="flex items-center gap-2 mt-6 mb-5">
      <History className="w-5 h-5" style={{ color: pc }} />
      <h2 className="text-white text-base font-bold uppercase tracking-wide">Recently Played</h2>
      <div className="flex-1 h-[1px] ml-3" style={{ background: `${pc}33` }} />
    </div>
    <div className="space-y-1.5">
      {history.slice(0, 10).map((item, i) => (
        <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] hover:bg-white/[0.05] transition border border-white/[0.03]">
          {item.song?.art ? (
            <img src={item.song.art} alt="" className="w-10 h-10 rounded-lg object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-white/5">
              <Music className="w-4 h-4 text-white/15" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{item.song?.title || "Unknown"}</p>
            <p className="text-white/35 text-xs truncate">{item.song?.artist || ""}</p>
          </div>
          <span className="text-white/15 text-xs flex-shrink-0">
            {item.played_at ? new Date(item.played_at * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
          </span>
        </div>
      ))}
    </div>
  </section>
);

export const ChatSection = ({ chatMessages, chatInput, setChatInput, onSendChat, user, token, pc }) => {
  const chatEndRef = useRef(null);
  return (
    <section id="section-chat" className="py-8" data-testid="chat-section">
      <Divider pc={pc} />
      <div className="flex items-center gap-2 mt-6 mb-5">
        <MessageCircle className="w-5 h-5" style={{ color: pc }} />
        <h2 className="text-white text-base font-bold uppercase tracking-wide">Live Chat</h2>
        <div className="flex-1 h-[1px] ml-3" style={{ background: `${pc}33` }} />
      </div>
      <div className="bg-white/[0.02] border border-white/[0.05] rounded-2xl overflow-hidden">
        <div className="h-60 overflow-y-auto p-4 space-y-3" style={{ scrollbarWidth: "thin", scrollbarColor: `${pc}33 transparent` }}>
          {chatMessages.length === 0 && (
            <p className="text-white/25 text-sm text-center py-8">No messages yet. Start the conversation!</p>
          )}
          {chatMessages.map((msg, i) => (
            <div key={i} className="flex gap-2">
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white" style={{ background: `${pc}33` }}>
                {(msg.user_name || "?")[0].toUpperCase()}
              </div>
              <div>
                <span className="text-xs font-semibold" style={{ color: pc }}>{msg.user_name || "Listener"}</span>
                <p className="text-white/70 text-sm">{msg.message}</p>
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
        <div className="p-3 border-t border-white/[0.05] flex gap-2">
          {user || token ? (
            <>
              <input
                type="text" value={chatInput} onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && onSendChat()}
                placeholder="Type a message..."
                className="flex-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm placeholder-white/25 focus:outline-none"
                data-testid="station-chat-input"
              />
              <button onClick={onSendChat} className="p-2 rounded-xl text-white transition hover:brightness-110" style={{ background: pc }} data-testid="station-chat-send">
                <Send className="w-4 h-4" />
              </button>
            </>
          ) : (
            <p className="text-white/25 text-sm py-2 text-center w-full">
              <a href="/login" className="underline" style={{ color: pc }}>Log in</a> to chat
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export const RequestsSection = ({ requestText, setRequestText, dedicateText, setDedicateText, dedicateTo, setDedicateTo, onSendRequest, requestSuccess, showDedicate, pc }) => (
  <section id="section-requests" className="py-8" data-testid="requests-section">
    <Divider pc={pc} />
    <div className="flex items-center gap-2 mt-6 mb-5">
      <Music className="w-5 h-5" style={{ color: pc }} />
      <h2 className="text-white text-base font-bold uppercase tracking-wide">Song Requests</h2>
      <div className="flex-1 h-[1px] ml-3" style={{ background: `${pc}33` }} />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="bg-white/[0.02] border border-white/[0.05] rounded-2xl p-5">
        <h3 className="text-white font-semibold text-sm mb-3">Request a Song</h3>
        <input type="text" value={requestText} onChange={e => setRequestText(e.target.value)}
          placeholder="Song name or artist..."
          className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder-white/25 focus:outline-none mb-3"
          data-testid="station-request-input" />
        <button onClick={() => onSendRequest("request")} disabled={!requestText}
          className="w-full py-2.5 rounded-xl text-white text-sm font-medium transition disabled:opacity-30 hover:brightness-110"
          style={{ background: pc }} data-testid="station-request-submit">
          Send Request
        </button>
      </div>
      {showDedicate && (
        <div className="bg-white/[0.02] border border-white/[0.05] rounded-2xl p-5">
          <h3 className="text-white font-semibold text-sm mb-3">Send a Dedication</h3>
          <input type="text" value={dedicateText} onChange={e => setDedicateText(e.target.value)}
            placeholder="Song name..."
            className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder-white/25 focus:outline-none mb-2"
            data-testid="station-dedicate-song" />
          <input type="text" value={dedicateTo} onChange={e => setDedicateTo(e.target.value)}
            placeholder="Dedicate to..."
            className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm placeholder-white/25 focus:outline-none mb-3"
            data-testid="station-dedicate-to" />
          <button onClick={() => onSendRequest("dedication")} disabled={!dedicateText || !dedicateTo}
            className="w-full py-2.5 rounded-xl text-white text-sm font-medium transition disabled:opacity-30 hover:brightness-110"
            style={{ background: pc }} data-testid="station-dedicate-submit">
            Send Dedication
          </button>
        </div>
      )}
    </div>
    {requestSuccess && (
      <div className="mt-3 text-sm px-4 py-2 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400">{requestSuccess}</div>
    )}
  </section>
);

export const AboutSection = ({ stationName, config, tagline, pc }) => (
  <section id="section-about" className="py-8" data-testid="about-section">
    <Divider pc={pc} />
    <div className="flex items-center gap-2 mt-6 mb-5">
      <Radio className="w-5 h-5" style={{ color: pc }} />
      <h2 className="text-white text-base font-bold uppercase tracking-wide">About {stationName}</h2>
      <div className="flex-1 h-[1px] ml-3" style={{ background: `${pc}33` }} />
    </div>
    <div className="bg-white/[0.02] border border-white/[0.05] rounded-2xl p-6">
      {config.page_description ? (
        <p className="text-white/60 text-base leading-relaxed">{config.page_description}</p>
      ) : tagline ? (
        <p className="text-white/60 text-base">{tagline}</p>
      ) : (
        <p className="text-white/25 text-sm">Welcome to {stationName}. Tune in and enjoy the music!</p>
      )}
    </div>
  </section>
);

import SmartVideoPlayer from "./SmartVideoPlayer";

// Auto-convert YouTube/Twitch URLs to embed format (kept for backwards compat)
const toEmbedUrl = (url) => {
  if (!url) return url;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const ytLive = url.match(/youtube\.com\/live\/([\w-]+)/);
  if (ytLive) return `https://www.youtube.com/embed/${ytLive[1]}`;
  const twMatch = url.match(/twitch\.tv\/(\w+)/);
  if (twMatch && !url.includes("player.twitch.tv")) return `https://player.twitch.tv/?channel=${twMatch[1]}&parent=${window.location.hostname}`;
  return url;
};

export const LiveVideoSection = ({ videoUrl, pc }) => (
  <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-5" data-testid="live-video-widget">
    <h3 className="text-white font-bold text-xs uppercase tracking-widest mb-3 flex items-center gap-2">
      <Tv className="w-4 h-4" style={{ color: pc }} /> Live Video
    </h3>
    <div className="aspect-video rounded-xl overflow-hidden bg-black">
      <SmartVideoPlayer url={videoUrl} pc={pc} />
    </div>
  </div>
);
